/**
 * DOCX paragraph + footnote helpers (N2 counsel export).
 */
import { inlineCitationMarker } from './footnoteAnchorPolicy';
import {
  Paragraph,
  TextRun,
  HeadingLevel,
  PageBreak,
  FootnoteReferenceRun,
  ExternalHyperlink,
  AlignmentType,
} from 'docx';
import type { DataSource } from '../../../types/siteRag';
import { DOCX_COLORS, DOCX_FONT, DOCX_SIZES } from './docxStyles';
import { docxPlatformCreditParagraph } from '../../reportBrand/docxPlatformCredit';

export type FootnoteMap = Record<
  number,
  { children: Paragraph[] }
>;

/** House style — EN-DASH ONLY. Swap em-dash (U+2014) → en-dash (U+2013) on
 *  rendered counsel/portfolio DOCX text so it matches the report house style
 *  (the in-app DOCX/PDF/PPTX enforce the same no-em-dash rule). Applied at the
 *  text-bearing helper boundary (cover lines, headings, body, callouts, list
 *  items, inline cited runs). */
export function enDashOnly(text: string): string {
  return text ? text.replace(/—/g, '–') : text;
}

export function slugifyFilenamePart(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
}

export function coverLine(
  text: string,
  opts?: { bold?: boolean; size?: number; color?: string },
): Paragraph {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 120 },
    children: [
      new TextRun({
        text: enDashOnly(text),
        font: DOCX_FONT,
        size: opts?.size ?? DOCX_SIZES.body,
        bold: opts?.bold,
        color: opts?.color ?? DOCX_COLORS.textPrimary,
      }),
    ],
  });
}

/**
 * The Gateway Power platform credit as the LAST line of a house-style cover
 * (counsel / portfolio / roadmap Word exports): the small G mark + "Datacrate ·
 * A Gateway Power platform", centred like the cover lines above it, one size
 * down and muted so it never competes with the cover title.
 */
export function coverPlatformCreditLine(): Paragraph {
  return docxPlatformCreditParagraph({
    font: DOCX_FONT,
    color: DOCX_COLORS.textMuted,
    sizeHalfPoints: DOCX_SIZES.small,
    alignment: AlignmentType.CENTER,
    spacing: { before: 240, after: 120 },
  });
}

export function heading1(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    children: [new TextRun({ text: enDashOnly(text), font: DOCX_FONT, size: DOCX_SIZES.h1, bold: true, color: DOCX_COLORS.brand })],
    spacing: { before: 240, after: 120 },
    // Orphan control — keep the heading on the same page as the first
    // line of the section it introduces, and never split the heading
    // itself across a page boundary.
    keepNext: true,
    keepLines: true,
  });
}

export function heading2(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    children: [new TextRun({ text: enDashOnly(text), font: DOCX_FONT, size: DOCX_SIZES.h2, bold: true, color: DOCX_COLORS.textSecondary })],
    spacing: { before: 200, after: 80 },
    keepNext: true,
    keepLines: true,
  });
}

export function heading3(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_3,
    children: [new TextRun({ text: enDashOnly(text), font: DOCX_FONT, size: DOCX_SIZES.h3, bold: true, color: DOCX_COLORS.textSecondary })],
    spacing: { before: 160, after: 60 },
    keepNext: true,
    keepLines: true,
  });
}

export function bodyParagraph(text: string): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text: enDashOnly(text), font: DOCX_FONT, size: DOCX_SIZES.body, color: DOCX_COLORS.textPrimary })],
    spacing: { after: 80, line: 276 },
  });
}

export function calloutParagraph(label: string, body: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({ text: `${enDashOnly(label)}: `, font: DOCX_FONT, size: DOCX_SIZES.body, bold: true, color: DOCX_COLORS.brand }),
      new TextRun({ text: enDashOnly(body), font: DOCX_FONT, size: DOCX_SIZES.body, color: DOCX_COLORS.textPrimary }),
    ],
    spacing: { before: 120, after: 120 },
    shading: { fill: DOCX_COLORS.tableHeader },
  });
}

export function pageBreakParagraph(): Paragraph {
  return new Paragraph({ children: [new PageBreak()] });
}

export function numberedItem(
  number: number,
  title: string,
  body: string,
): Paragraph[] {
  return [
    new Paragraph({
      children: [
        new TextRun({ text: `${number}. ${enDashOnly(title)}`, font: DOCX_FONT, size: DOCX_SIZES.body, bold: true }),
      ],
      spacing: { before: 120, after: 40 },
    }),
    new Paragraph({
      children: [new TextRun({ text: enDashOnly(body), font: DOCX_FONT, size: DOCX_SIZES.body, color: DOCX_COLORS.textSecondary })],
      spacing: { after: 80 },
      indent: { left: 360 },
    }),
  ];
}

function refFootnoteBody(ref: DataSource, index: number): Paragraph {
  const title = (ref.source ?? ref.field ?? `Reference ${index}`).trim();
  const url = (ref.url ?? '').trim();
  const children: (TextRun | ExternalHyperlink)[] = [
    new TextRun({ text: title, font: DOCX_FONT, size: DOCX_SIZES.footnote }),
  ];
  if (url.startsWith('http')) {
    children.push(new TextRun({ text: ' – ', font: DOCX_FONT, size: DOCX_SIZES.footnote }));
    children.push(new ExternalHyperlink({
      link: url,
      children: [new TextRun({ text: url, font: DOCX_FONT, size: DOCX_SIZES.footnote, color: DOCX_COLORS.brand, underline: {} })],
    }));
  }
  return new Paragraph({ children });
}

export function buildFootnotesFromReferences(refs: DataSource[]): FootnoteMap {
  const map: FootnoteMap = {};
  refs.forEach((ref, i) => {
    const id = i + 1;
    map[id] = { children: [refFootnoteBody(ref, id)] };
  });
  return map;
}

/** The numbered citation-marker forms `inlineRunsFromText` lifts to footnotes:
 *  bare `[N]`, `[verified Ref N]` and `[Ref N]`. Exported so the markdown
 *  paragraph builder can count a paragraph's markers before splitting it. */
export const DOCX_CITATION_MARKER = /\[(\d+)\]|\[(?:verified|inferred|unverified) Ref (\d+)\]|\[Ref\s+(\d+)\]/gi;

export function inlineRunsFromText(
  text: string,
  footnotes: FootnoteMap,
  opts?: { bold?: boolean; italics?: boolean; /** Render markers as inline superscript text, no footnote anchor (see footnoteAnchorPolicy). */ citationsInline?: boolean },
): (TextRun | FootnoteReferenceRun | ExternalHyperlink)[] {
  // House style — en-dash only. Normalise BEFORE the citation-marker split so
  // every narrative slice carries en-dashes; the citation regex is unaffected.
  text = enDashOnly(text);
  const runs: (TextRun | FootnoteReferenceRun | ExternalHyperlink)[] = [];
  // Citation tokens, in precedence order:
  //   m[2] → bare `[N]`
  //   m[3] → `[verified Ref N]`
  //   m[4] → bare `[Ref N]` (case-insensitive) — so this DOCX path
  //          behaves identically to walkCitationText / reportDocxExport,
  //          which both resolve a bare "[Ref N]" to a footnote reference.
  const re = /(\[(\d+)\]|\[(?:verified|inferred|unverified) Ref (\d+)\]|\[Ref\s+(\d+)\]|https?:\/\/[^\s)\]]+|doc:\/\/[^\s)\]]+)/gi;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      runs.push(new TextRun({
        text: text.slice(last, m.index),
        font: DOCX_FONT,
        size: DOCX_SIZES.body,
        bold: opts?.bold,
        italics: opts?.italics,
      }));
    }
    const token = m[0];
    if (m[2] || m[3] || m[4]) {
      const n = parseInt(m[2] ?? m[3] ?? m[4], 10);
      if (Number.isFinite(n) && n >= 1) {
        if (opts?.citationsInline) {
          runs.push(new TextRun({ text: inlineCitationMarker(n), font: DOCX_FONT, size: DOCX_SIZES.body, bold: opts?.bold, italics: opts?.italics }));
        } else {
          if (!footnotes[n]) {
            footnotes[n] = { children: [new Paragraph({ children: [new TextRun({ text: `(Reference ${n})`, font: DOCX_FONT, size: DOCX_SIZES.footnote })] })] };
          }
          runs.push(new FootnoteReferenceRun(n));
        }
      } else {
        runs.push(new TextRun({ text: token, font: DOCX_FONT, size: DOCX_SIZES.body }));
      }
    } else if (/^doc:\/\//i.test(token)) {
      const docId = token.slice('doc://'.length).split('?')[0];
      const pageMatch = token.match(/page=(\d+)/);
      const href = `/docs/${encodeURIComponent(docId)}${pageMatch ? `?page=${pageMatch[1]}` : ''}`;
      runs.push(new ExternalHyperlink({
        link: href,
        children: [new TextRun({ text: token, font: DOCX_FONT, size: DOCX_SIZES.body, color: DOCX_COLORS.brand, underline: {} })],
      }));
    } else if (/^https?:\/\//i.test(token)) {
      runs.push(new ExternalHyperlink({
        link: token,
        children: [new TextRun({ text: token, font: DOCX_FONT, size: DOCX_SIZES.body, color: DOCX_COLORS.brand, underline: {} })],
      }));
    }
    last = m.index + token.length;
  }
  if (last < text.length) {
    runs.push(new TextRun({
      text: text.slice(last),
      font: DOCX_FONT,
      size: DOCX_SIZES.body,
      bold: opts?.bold,
      italics: opts?.italics,
    }));
  }
  if (runs.length === 0) {
    runs.push(new TextRun({ text: text || '', font: DOCX_FONT, size: DOCX_SIZES.body }));
  }
  return runs;
}

export function bodyParagraphWithRuns(
  runs: (TextRun | FootnoteReferenceRun | ExternalHyperlink)[],
): Paragraph {
  return new Paragraph({ children: runs, spacing: { after: 80, line: 276 } });
}
